import React, { useState, useEffect, useLayoutEffect } from 'react';
import {
  Routes,
  Route,
  useNavigate,
  useParams,
  useLocation as useRouterLocation,
} from 'react-router-dom';
import { Plus, AlertTriangle } from 'lucide-react';
import { Toaster } from 'react-hot-toast';

import { debounce } from './utils/debounce';
import { LocationCard } from './components/LocationCard';
import { TravelLocationCard } from "./components/TravelLocationCard";
import { Filters } from './components/Filters';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AddLocation } from './components/AddLocation';
import { AuthPrompt } from './components/AuthPrompt';
import { EmptyState } from './components/EmptyState';
import { Pagination } from './components/Pagination';
import { Location, FilterState, UserLocation, ViewMode, VisitStats } from './types';
import { getLocations, LOCATIONS_PER_PAGE } from './utils/api';
import { addLocation, getVisitStats } from './utils/supabase';
import { Map } from './components/Map';
import { LocationProfile } from './components/LocationProfile';
import LocationProfileSkeleton from './components/LocationProfileSkeleton';
import { AdminPanel } from './admin/AdminPanel';
import { LocationProvider, useLocation } from './context/LocationContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { LandingPage } from './components/LandingPage';
import AuthCallback from './components/Auth/AuthCallback';
import ResetPassword from './components/Auth/ResetPassword';


export function MainPage() {
  const { userLocation, setUserLocation } = useLocation();
  const { user, loading: authLoading } = useAuth(); // Добавляем authLoading
  const [locations, setLocations] = useState<Location[]>([]);
  const [totalLocations, setTotalLocations] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Флаг первой загрузки
  const CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes
  const [locationCache, setLocationCache] = useState<Record<string, Record<number, { locations: Location[]; total: number; timestamp: number }>>>({});
  const [visitStats, setVisitStats] = useState<Record<string, VisitStats>>({});
  const [filters, setFilters] = useState<FilterState>({
    category: 'all',
    searchQuery: '',
    radius: 150,
    tags: [],
    sortBy: 'distance',
  });

  const navigate = useNavigate();
  const { pathname } = useRouterLocation();

  // Ключ для sessionStorage
  const STORAGE_KEY = 'mainPageState';

  // Функция для сохранения состояния страницы
  const savePageState = () => {
    if (pathname !== '/app') return;
    
    const state = {
      filters,
      currentPage,
      viewMode,
      scrollY: window.scrollY,
      timestamp: Date.now()
    };
    
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save page state', e);
    }
  };

  // Функция для восстановления состояния страницы
  const restorePageState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (!saved) return null;

      const state = JSON.parse(saved);
      
      // Проверяем, что состояние не слишком старое (максимум 1 час)
      const maxAge = 60 * 60 * 1000; // 1 час
      if (Date.now() - state.timestamp > maxAge) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return state;
    } catch (e) {
      console.error('Failed to restore page state', e);
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  // Восстанавливаем состояние при каждом заходе на главную страницу
  useEffect(() => {
    if (pathname === '/app') {
      const savedState = restorePageState();
      if (savedState) {
        if (savedState.filters) {
          setFilters(savedState.filters);
        }
        if (savedState.currentPage) {
          setCurrentPage(savedState.currentPage);
        }
        if (savedState.viewMode) {
          setViewMode(savedState.viewMode);
        }
        
        // Восстанавливаем скролл после рендера
        if (typeof savedState.scrollY === 'number' && savedState.scrollY > 0) {
          setTimeout(() => {
            window.scrollTo(0, savedState.scrollY);
          }, 100);
        }
      }
    }
  }, [pathname]);

  // Сохраняем состояние при изменении фильтров, страницы или режима просмотра
  useEffect(() => {
    if (pathname === '/app') {
      savePageState();
    }
  }, [filters, currentPage, viewMode, pathname]);

  // Отслеживаем скролл и сохраняем состояние с дебаунсом
  useEffect(() => {
    if (pathname !== '/app') return;

    const debouncedSave = debounce(() => {
      savePageState();
    }, 200);

    const handleScroll = () => {
      debouncedSave();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      debouncedSave.cancel();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [filters, currentPage, viewMode, pathname]);

  // Сохраняем состояние при размонтировании компонента
  useEffect(() => {
    return () => {
      if (pathname === '/app') {
        savePageState();
      }
    };
  }, [filters, currentPage, viewMode, pathname]);

  // Загрузка геолокации пользователя
  useEffect(() => {
    const detectLocation = async () => {
      if (navigator.geolocation) {
        const geolocationOptions = {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000,
        };

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            try {
              const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=be`,
              );
              const data = await response.json();
              setUserLocation({
                lat: latitude,
                lng: longitude,
                city: data.city || 'Невядомы горад',
                source: 'gps'
              });
            } catch (error) {
              console.error('Памылка атрымання дадзеных пра месцазнаходжанне:', error);
              setUserLocation({
                lat: 53.9,
                lng: 27.5667,
                city: 'Мінск',
                source: 'gps'
              });
            }
          },
          async () => {
            try {
              const res = await fetch('https://ipapi.co/json/');
              const ipData = await res.json();
              setUserLocation({
                lat: ipData.latitude || 53.9,
                lng: ipData.longitude || 27.5667,
                city: ipData.city || 'Мінск',
                source: 'ip'
              });
            } catch (_) {
              setUserLocation({
                lat: 53.9,
                lng: 27.5667,
                city: 'Мінск',
                source: 'ip'
              });
            }
          },
          geolocationOptions,
        );
      } else {
        try {
          const res = await fetch('https://ipapi.co/json/');
          const ipData = await res.json();
          setUserLocation({
            lat: ipData.latitude || 53.9,
            lng: ipData.longitude || 27.5667,
            city: ipData.city || 'Мінск',
            source: 'ip'
          });
        } catch (_) {
          setUserLocation({
            lat: 53.9,
            lng: 27.5667,
            city: 'Мінск',
            source: 'ip'
          });
        }
      }
    };

    detectLocation();
  }, []);

  const getFilterKey = (f: FilterState) =>
    `${f.category}|${f.searchQuery}|${[...f.tags].sort().join(',')}|${f.sortBy}|${f.radius}`;

  // 🔧 ИСПРАВЛЕННАЯ ФУНКЦИЯ ЗАГРУЗКИ ДАННЫХ
  const loadLocations = async (forceReload = false) => {
    console.log('🔄 loadLocations called:', {
      forceReload,
      isInitialLoad,
      authLoading,
      user: !!user,
      filters,
      currentPage,
      timestamp: new Date().toISOString()
    });

    const key = getFilterKey(filters);
    const cached = locationCache[key]?.[currentPage];
    
    // Используем кэш только если не принудительная перезагрузка и кэш свежий
    if (!forceReload && cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      console.log('📦 Using cached data for:', key, currentPage);
      setLocations(cached.locations);
      setTotalLocations(cached.total);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🌐 Making API request to Supabase...');
      const { locations: data, total } = await getLocations(
        false,
        currentPage,
        LOCATIONS_PER_PAGE,
        filters
      );

      console.log('✅ API response received:', {
        locationsCount: data.length,
        total,
        timestamp: new Date().toISOString()
      });

      const statsEntries = await Promise.all(
        data.map(async (loc) => [loc.id, await getVisitStats(loc.id)] as const)
      );
      setVisitStats((prev) => ({ ...prev, ...Object.fromEntries(statsEntries) }));

      setLocations(data);
      setTotalLocations(total);
      
      // Обновляем кэш
      setLocationCache((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          [currentPage]: { locations: data, total, timestamp: Date.now() },
        },
      }));

      console.log('💾 Data cached and state updated');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Не атрымалася загрузіць лакацыі';
      console.error('❌ Error loading locations:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  };

  // 🔧 ИСПРАВЛЕННЫЙ useEffect ДЛЯ ЗАГРУЗКИ ДАННЫХ
  useEffect(() => {
    console.log('🎯 Main useEffect triggered:', {
      authLoading,
      isInitialLoad,
      filters,
      currentPage,
      timestamp: new Date().toISOString()
    });

    // Ждем завершения проверки аутентификации
    if (authLoading) {
      console.log('⏳ Waiting for auth to complete...');
      return;
    }

    // Принудительная перезагрузка при первой загрузке или смене пользователя
    const forceReload = isInitialLoad;
    
    console.log('🚀 Starting loadLocations with forceReload:', forceReload);
    loadLocations(forceReload);
  }, [authLoading, filters, currentPage, isInitialLoad]); // Добавили authLoading в зависимости

  // 🔧 ДОПОЛНИТЕЛЬНЫЙ useEffect ДЛЯ ОТСЛЕЖИВАНИЯ СМЕНЫ ПОЛЬЗОВАТЕЛЯ
  useEffect(() => {
    console.log('👤 User state changed:', {
      hasUser: !!user,
      userEmail: user?.email,
      timestamp: new Date().toISOString()
    });

    // Если пользователь изменился (вошел/вышел), очищаем кэш и перезагружаем
    if (!authLoading) {
      console.log('🔄 User changed, clearing cache and reloading...');
      setLocationCache({}); // Очищаем кэш
      setIsInitialLoad(true); // Помечаем как первую загрузку
      loadLocations(true); // Принудительная перезагрузка
    }
  }, [user?.id, authLoading]); // Отслеживаем изменение ID пользователя

  const handleAddLocation = async (newLocation: Omit<Location, 'id'>) => {
    try {
      const { data, error } = await addLocation(newLocation);
      if (error) {
        throw new Error(error);
      }
      if (data) {
        setShowAddLocation(false);
        setError(null);
        // Очищаем кэш и перезагружаем данные
        setLocationCache({});
        loadLocations(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Не атрымалася дадаць лакацыю';
      setError(errorMessage);
      throw err;
    }
  };

  const calculateDistance = (
    userLoc: UserLocation,
    locationCoords: { lat: number; lng: number },
  ): number => {
    if (!window.ymaps?.coordSystem?.geo) {
      return 0;
    }
    return (
      window.ymaps.coordSystem.geo.getDistance(
        [userLoc.lat, userLoc.lng],
        [locationCoords.lat, locationCoords.lng],
      ) / 1000
    );
  };

  const filteredAndSortedLocations = locations
    .filter((location) => {
      if (userLocation && filters.radius !== 800) {
        const distance = calculateDistance(userLocation, location.coordinates);
        if (distance > filters.radius) {
          return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      if (filters.sortBy === 'newest') {
        return (
          new Date(b.created_at || '').getTime() -
          new Date(a.created_at || '').getTime()
        );
      }

      if (filters.sortBy === 'popular') {
        const visitsA = visitStats[a.id]?.total || 0;
        const visitsB = visitStats[b.id]?.total || 0;
        return visitsB - visitsA;
      }

      if (filters.sortBy === 'rating') {
        const ratingA = visitStats[a.id]?.total
          ? visitStats[a.id].likes / visitStats[a.id].total
          : 0;
        const ratingB = visitStats[b.id]?.total
          ? visitStats[b.id].likes / visitStats[b.id].total
          : 0;
        return ratingB - ratingA;
      }

      if (!userLocation) return 0;
      const distanceA = calculateDistance(userLocation, a.coordinates);
      const distanceB = calculateDistance(userLocation, b.coordinates);
      return distanceA - distanceB;
    });

  const totalPages = Math.ceil(totalLocations / LOCATIONS_PER_PAGE);
  const paginatedLocations = filteredAndSortedLocations;

  const handleLocationSelect = (location: Location) => {
    // Сохраняем текущее состояние перед переходом
    savePageState();
    setSelectedLocation(location);
    navigate(`/l/${location.short_id}`);
  };

  const handleLocationClose = () => {
    setSelectedLocation(null);
    
    // Используем history.back() вместо navigate('/app') для сохранения скролла
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/app');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleTagClick = (tag: string) => {
    setFilters(prev => (prev.tags.includes(tag) ? prev : { ...prev, tags: [...prev.tags, tag] }));
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Toaster position="top-center" />
      <Header userLocation={userLocation} />

      <main className="container mx-auto px-4 py-8 flex-grow">
        {window.dbConnectionError && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle size={20} />
              <p className="font-medium">
                Увага! Праблемы з падключэннем да базы дадзеных.
              </p>
            </div>
            <p className="mt-1 text-yellow-700">
              Паказваюцца тэставыя дадзеныя. Некаторыя функцыі могуць быць
              недаступныя.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-4 mb-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div className="max-w-2xl">
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                <span className="hidden md:inline">
                  Ласкава запрашаем у "Мой Куток" - месца, дзе вы можаце знайсці
                  і падзяліцца цікавымі прыроднымі лакацыямі Беларусі. Тут
                  збіраюцца не толькі экасцежкі, месцы для кемпінгу, пікніка і
                  назірання за зоркамі, але і проста прыгожыя куткі прыроды для
                  шпацыраў і адпачынку. Далучайцеся да нашай супольнасці,
                  дадавайце свае любімыя месцы і адкрывайце новыя куткі роднай
                  прыроды!
                </span>
                <span className="md:hidden">
                  Знаходзьце цікавыя прыродныя лакацыі Беларусі: экасцежкі,
                  месцы для кемпінгу, пікніка і назірання за зоркамі. Дадавайце
                  свае любімыя месцы і адкрывайце новыя куткі роднай прыроды!
                </span>
              </p>
            </div>
            <button
              onClick={() => {
                if (user) {
                  setShowAddLocation(true);
                } else {
                  setShowAuthPrompt(true);
                }
              }}
              className="flex items-center justify-center gap-2 px-6 py-2 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors w-full md:w-auto"
            >
              <Plus size={20} />
              Дадаць лакацыю
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <p className="font-medium">Памылка:</p>
            <p>{error}</p>
          </div>
        )}

        <Filters
          filters={filters}
          viewMode={viewMode}
          onFilterChange={setFilters}
          onViewModeChange={setViewMode}
        />

        {viewMode === 'list' ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <LocationCard
                    key={index}
                    location={{} as Location}
                    isLoading={true}
                    onSelect={() => {}}
                  />
                ))
              ) : paginatedLocations.length === 0 ? (
                <div className="col-span-full">
                  <EmptyState />
                </div>
              ) : (
                paginatedLocations.map((location) => (
                  <TravelLocationCard
                    key={location.id}
                    location={location}
                    userLocation={userLocation}
                    onSelect={handleLocationSelect} 
                    onTagClick={handleTagClick}
                  />
                ))
              )}
            </div>
            {!isLoading && filteredAndSortedLocations.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        ) : (
          <div className="h-[calc(100vh-16rem)] rounded-xl overflow-hidden">
            <Map
              center={
                userLocation
                  ? [userLocation.lat, userLocation.lng]
                  : [53.9, 27.5667]
              }
              locations={paginatedLocations}
              onLocationClick={handleLocationSelect}
              showUserLocation={Boolean(userLocation)}
              height="100%"
            />
          </div>
        )}
      </main>

      <Footer />

      {showAddLocation && (
        <AddLocation
          onAdd={handleAddLocation}
          onClose={() => setShowAddLocation(false)}
        />
      )}

      {showAuthPrompt && (
        <AuthPrompt
          isOpen={showAuthPrompt}
          onClose={() => setShowAuthPrompt(false)}
        />
      )}

      {selectedLocation && (
        <LocationProfile
          location={selectedLocation}
          onClose={handleLocationClose}
          userLocation={userLocation}
        />
      )}
    </div>
  );
}

function LocationRoute() {
  const { shortId } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState<Location | null>(null);
  const { userLocation } = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLocation = async () => {
      try {
        setIsLoading(true);
        const { locations } = await getLocations();
        const foundLocation = locations.find((loc) => loc.short_id === shortId);
        if (foundLocation) {
          setLocation(foundLocation);
        } else {
          setError('Лакацыя не знойдзена');
          navigate('/app');
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Не атрымалася загрузіць лакацыю',
        );
        navigate('/app');
      } finally {
        setIsLoading(false);
      }
    };

    loadLocation();
  }, [shortId, navigate]);

  const handleLocationClose = () => {
    // Используем history.back() для возврата на предыдущую страницу с сохранением скролла
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/app');
    }
  };

  if (isLoading) {
    return <LocationProfileSkeleton />;
  }

  if (!location) {
    return null;
  }

  return (
    <LocationProfile
      location={location}
      onClose={handleLocationClose}
      userLocation={userLocation}
    />
  );
}

// Компонент для обработки ошибок на главной странице
function ErrorBoundaryPage() {
  const { pathname, search, hash } = useRouterLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Проверяем, есть ли ошибки в URL параметрах
    const params = new URLSearchParams(search);
    const hashParams = new URLSearchParams(hash.replace('#', ''));
    
    const error = params.get('error') || hashParams.get('error');
    const errorCode = params.get('error_code') || hashParams.get('error_code');
    const type = params.get('type') || hashParams.get('type');
    
    // Если есть ошибка OTP expired и тип операции - редиректим на callback
    if ((errorCode === 'otp_expired' || error === 'access_denied') && type) {
      console.log('Redirecting OTP expired error to callback handler');
      navigate(`/auth/callback${search}${hash}`, { replace: true });
      return;
    }
    
    // Если есть другие ошибки аутентификации - показываем их
    if (error) {
      console.log('Auth error on main page:', error);
      // Можно показать toast или другое уведомление
    }
  }, [pathname, search, hash, navigate]);
  
  return <MainPage />;
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/app" element={<ErrorBoundaryPage />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/l/:shortId" element={<LocationRoute />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;